// src/padplanner/LibraryPage.jsx
//
// /padplanner/library: the template library (S3). The sets on the left (the
// AI PRIME standard sets, then the organization's own, every version), the
// set picked on the right with its templates.
//
// WHO SEES WHAT. Everyone with PadPlanner reads the published and retired
// sets. A library admin (org_admin with can_edit_site_plans, never through a
// support session: S3 decisions D2, D3) also sees drafts and gets the
// controls. The server says which (can_manage) and refuses the writes itself;
// controls a reader cannot use are not rendered.
//
// THE LIFECYCLE THE CONTROLS FOLLOW (migration 149). A set is born a draft;
// only a draft's name and templates change. Publishing is permanent: a
// published set changes only by a new version (a draft copied from it), and
// is retired rather than deleted. A draft is discarded. Plans keep the
// version they were created with, retired or not. The org's default names a
// lineage; new plans pin its latest published version (D9).
//
// The standard set is read-only here for everyone: it is written only by
// migrations (D1).
//
// CLEARANCE RULES (S5b) are listed below the sets: the org's one rule-set
// lineage, with the same lifecycle and its own permissions (RuleSets.jsx).
//
// APPROVAL ROUTES (S6a2) are listed below the rules: the steps a submitted
// plan goes through, managed by an org_admin (ApprovalRoutes.jsx).
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import PadPlannerGate from "./PadPlannerGate";
import SidePanel, { SectionLabel } from "./SidePanel";
import { TemplatePreview } from "./TemplatesTab";
import {
  listTemplateSets, getTemplateSet, createTemplateSet, updateTemplateSet, discardTemplateSet, newTemplateSetVersion,
  publishTemplateSet, retireTemplateSet, addTemplate, updateTemplate, removeTemplate, setDefaultTemplateLineage,
  listRuleSets, createRuleSet, listRoutes, createRoute, updateRoute, archiveRoute,
} from "../api/padplannerApi";
import { RuleSetList, RuleSetDetail } from "./RuleSets";
import { RouteList, RouteDetail, RouteForm } from "./ApprovalRoutes";
import ReadFailed from "./ReadFailed";
import { refusalText } from "./planRefusals";
import { CATEGORY_LABELS, TEMPLATE_CATEGORIES, NO_SOURCE_TEXT, setChipLabel, templateDims } from "./templateVocabulary";
import { PADPLANNER_DISPLAY_NAME } from "../constants/analyticsProducts";

export default function LibraryPage() {
  return (
    <PadPlannerGate section="library">
      <Library />
    </PadPlannerGate>
  );
}

export const READ_ONLY_TEXT = "Only an organization admin can change the template library.";
export const STATUS_WORDS = Object.freeze({ draft: "Draft", published: "Published", retired: "Retired" });

const field = "rounded border border-bg-border bg-bg px-2 py-1.5 text-[13px] text-fg-primary";

function Library() {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [failed, setFailed] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);

  const [rules, setRules] = useState(null);
  const [rulesFailed, setRulesFailed] = useState(false);
  const [ruleSetId, setRuleSetId] = useState(null);
  // Approval routes: the route shown, or {form: 'new'|'edit'}.
  const [routes, setRoutes] = useState(null);
  const [routesFailed, setRoutesFailed] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [routeView, setRouteView] = useState(null);
  // One detail at a time: a template set, a rule set or a route.
  const select = useCallback((id) => { setError(null); setRuleSetId(null); setRouteView(null); setSelectedId(id); }, []);
  const selectRules = useCallback((id) => { setError(null); setSelectedId(null); setDetail(null); setRouteView(null); setRuleSetId(id); }, []);
  const selectRoute = useCallback((view) => { setError(null); setSelectedId(null); setDetail(null); setRuleSetId(null); setRouteView(view); }, []);
  const reloadRoutes = useCallback(() => listRoutes({ includeArchived: showArchived })
    .then((r) => { setRoutes(r); setRoutesFailed(false); return r; })
    .catch(() => { setRoutesFailed(true); return null; }), [showArchived]);
  useEffect(() => { reloadRoutes(); }, [reloadRoutes]);
  /** A route write ({create}|{update, body}|{archive}): {value} or {error}; the list is refreshed after either. */
  const actRoutes = useCallback(async (op) => {
    try {
      const value = op.create ? await createRoute(op.create)
        : op.update ? await updateRoute(op.update, op.body) : await archiveRoute(op.archive);
      await reloadRoutes();
      if (op.archive && !showArchived) setRouteView(null);
      return { value };
    } catch (e) {
      await reloadRoutes();
      return { error: e };
    }
  }, [reloadRoutes, showArchived]);
  const shownRoute = routeView?.id ? (routes?.routes ?? []).find((r) => r.id === routeView.id) ?? null : null;
  const reloadRules = useCallback(() => listRuleSets()
    .then((r) => { setRules(r); setRulesFailed(false); return r; })
    .catch(() => { setRulesFailed(true); return null; }), []);
  useEffect(() => { reloadRules(); }, [reloadRules]);
  /** A rule-set write: {value} or {error}; the list is refreshed after either. */
  const actRules = useCallback(async (fn) => {
    try {
      const value = await fn();
      await reloadRules();
      if (value?.set && value.set.id !== ruleSetId) setRuleSetId(value.set.id);
      return { value };
    } catch (e) {
      await reloadRules();
      return { error: e };
    }
  }, [reloadRules, ruleSetId]);

  const reload = useCallback(() => listTemplateSets()
    .then((r) => { setList(r); setFailed(false); return r; })
    .catch(() => { setFailed(true); return null; }), []);
  useEffect(() => { reload(); }, [reload]);

  // A write answers the set as it now is and selects it; that is not fetched again.
  const loadedId = detail?.set.id ?? null;
  useEffect(() => {
    // Cleared only by a discard, which clears the detail itself.
    if (!selectedId || selectedId === loadedId) return undefined;
    let cancelled = false;
    getTemplateSet(selectedId)
      .then((r) => { if (!cancelled) setDetail(r); })
      .catch((e) => { if (!cancelled) { setDetail(null); setError(refusalText(e)); } });
    return () => { cancelled = true; };
  }, [selectedId, loadedId]);

  /** Run a write; on success adopt what it answered and refresh the list. */
  const act = useCallback(async (fn, { select } = {}) => {
    setError(null);
    try {
      const r = await fn();
      if (r?.set) {
        if (select !== false) setSelectedId(r.set.id);
        if (r.set.id === selectedId || select !== false) setDetail(r);
      }
      await reload();
      return r;
    } catch (e) {
      setError(refusalText(e));
      return null;
    }
  }, [reload, selectedId]);

  const canManage = list?.can_manage === true;
  const sets = list?.sets ?? [];
  const standard = sets.filter((s) => s.standard);
  const own = sets.filter((s) => !s.standard);

  const head = (
    <>
      <nav className="op-meta flex items-center gap-1" aria-label="Breadcrumb">
        <button type="button" className="hover:text-fg-primary" onClick={() => navigate({ to: "/padplanner" })}>{PADPLANNER_DISPLAY_NAME}</button>
        <span aria-hidden="true">/</span>
        <span className="text-fg-primary">Template library</span>
      </nav>
      <h1 className="font-display text-[19px] font-semibold leading-tight text-fg-primary">Template library</h1>
      {list && !canManage && <p className="op-meta" data-testid="pp-library-read-only">{READ_ONLY_TEXT}</p>}
      {canManage && <NewSetForm onCreate={(name) => act(() => createTemplateSet({ name }))} />}
    </>
  );

  return (
    <div className="flex h-full min-h-0">
      <SidePanel label="Template sets" head={head}>
        {failed && <ReadFailed what="the template library" onRetry={reload} testid="pp-sets-failed" />}
        {list && (
          <>
            <SetList title="AI PRIME standard" sets={standard} selectedId={selectedId} onSelect={select}
              empty="No standard set has been published yet." />
            <SetList title="Your organization" sets={own} selectedId={selectedId} onSelect={select}
              empty={canManage ? "No sets yet. Start one above." : "Your organization hasn't published a set yet."} />
          </>
        )}
        {rulesFailed && <ReadFailed what="the clearance rules" onRetry={reloadRules} testid="pp-rules-failed" />}
        {rules && (
          <RuleSetList list={rules} selectedId={ruleSetId} onSelect={selectRules} onCreate={async (body) => {
            const r = await actRules(() => createRuleSet(body));
            if (r.error) setError(`${r.error.message}${r.error.code ? ` (${r.error.code})` : ""}`);
            return !r.error;
          }} />
        )}
        {routesFailed && <ReadFailed what="the approval routes" onRetry={reloadRoutes} testid="pp-routes-failed" />}
        {routes && (
          <RouteList list={routes} selectedId={routeView?.id ?? null} onSelect={(id) => selectRoute({ id })}
            onCreate={() => selectRoute({ form: "new" })} showArchived={showArchived} onShowArchived={setShowArchived} />
        )}
      </SidePanel>
      <div className="min-w-0 flex-1 overflow-auto px-6 py-5">
        {error && <p className="mb-3 text-sm text-accent-red" role="alert">{error}</p>}
        {routeView?.form ? (
          <RouteForm key={`${routeView.form}:${routeView.id ?? ""}`} route={routeView.form === "edit" ? shownRoute : null} list={routes} act={actRoutes}
            onDone={(r) => selectRoute(r ? { id: r.id } : null)} onCancel={() => selectRoute(routeView.id ? { id: routeView.id } : null)} />
        ) : shownRoute ? (
          <RouteDetail key={shownRoute.id} route={shownRoute} list={routes} act={actRoutes} onEdit={() => selectRoute({ id: shownRoute.id, form: "edit" })} />
        ) : ruleSetId ? (
          <RuleSetDetail key={ruleSetId} setId={ruleSetId} list={rules} act={actRules} onDiscarded={() => setRuleSetId(null)} />
        ) : detail ? (
          <SetDetail key={detail.set.id} detail={detail} canManage={canManage}
            isDefault={!detail.set.standard && detail.set.lineage_id === list?.default_lineage_id}
            act={act}
            onDiscarded={() => { setSelectedId(null); setDetail(null); }} />
        ) : (
          <p className="op-meta">{selectedId ? "Loading…" : "Pick a set to see its templates."}</p>
        )}
      </div>
    </div>
  );
}

function NewSetForm({ onCreate }) {
  const [name, setName] = useState("");
  return (
    <form className="flex gap-2" aria-label="New set" onSubmit={async (e) => {
      e.preventDefault();
      if (!name.trim()) return;
      if (await onCreate(name.trim())) setName("");
    }}>
      <input className={`${field} min-w-0 flex-1`} value={name} maxLength={80} onChange={(e) => setName(e.target.value)}
        placeholder="New set name" aria-label="New set name" />
      <button type="submit" className="op-btn op-btn--primary op-btn--sm shrink-0" disabled={!name.trim()}>New set</button>
    </form>
  );
}

function SetList({ title, sets, selectedId, onSelect, empty }) {
  return (
    <section className="flex flex-col gap-1.5" aria-label={title}>
      <SectionLabel>{title}</SectionLabel>
      {sets.length === 0 && <p className="op-meta">{empty}</p>}
      {sets.map((s) => (
        <button key={s.id} type="button" aria-pressed={selectedId === s.id} data-testid="pp-library-set" onClick={() => onSelect(s.id)}
          className={`flex items-center justify-between gap-2 rounded border px-2.5 py-2 text-left text-[13px] ${selectedId === s.id ? "border-accent-action bg-accent-action/10" : "border-bg-border hover:border-fg-secondary"}`}>
          <span className="min-w-0 truncate text-fg-primary">{setChipLabel(s)}</span>
          <span className="flex shrink-0 items-center gap-1.5">
            {s.is_default && <span className="op-chip op-chip--accent px-1.5 py-0">Default</span>}
            <span className="op-chip px-1.5 py-0" data-status={s.status}>{STATUS_WORDS[s.status] ?? s.status}</span>
            <span className="op-mono text-[11px] text-fg-secondary">{s.template_count}</span>
          </span>
        </button>
      ))}
    </section>
  );
}

function SetDetail({ detail, canManage, isDefault, act, onDiscarded }) {
  const { set, templates } = detail;
  const manage = canManage && !set.standard;
  const draft = manage && set.status === "draft";
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(set.name);
  const [editingId, setEditingId] = useState(null);

  async function publish() {
    if (!window.confirm(`Publish ${setChipLabel(set)}? A published set can't be changed. To change it later, start a new version.`)) return;
    await act(() => publishTemplateSet(set.id));
  }
  async function retire() {
    if (!window.confirm(`Retire ${setChipLabel(set)}? Plans that use it keep it, but new plans won't get it.`)) return;
    await act(() => retireTemplateSet(set.id));
  }
  async function discard() {
    if (!window.confirm(`Discard ${setChipLabel(set)} and its templates? This can't be undone.`)) return;
    const r = await act(() => discardTemplateSet(set.id), { select: false });
    if (r?.discarded) onDiscarded();
  }

  return (
    <div className="flex max-w-[960px] flex-col gap-4" data-testid="pp-library-detail">
      <div className="flex flex-col gap-1.5">
        {renaming ? (
          <form className="flex gap-2" aria-label="Rename set" onSubmit={async (e) => {
            e.preventDefault();
            if (await act(() => updateTemplateSet(set.id, { name: name.trim() }))) setRenaming(false);
          }}>
            <input className={`${field} w-72`} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} aria-label="Set name" />
            <button type="submit" className="op-btn op-btn--primary op-btn--sm">Save</button>
            <button type="button" className="op-btn op-btn--sm" onClick={() => { setName(set.name); setRenaming(false); }}>Cancel</button>
          </form>
        ) : (
          <h2 className="font-display text-[17px] font-semibold text-fg-primary">{setChipLabel(set)}</h2>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <span className="op-chip px-1.5 py-0" data-testid="pp-library-status">{STATUS_WORDS[set.status] ?? set.status}</span>
          {set.standard && <span className="op-chip px-1.5 py-0">AI PRIME standard · read-only</span>}
          {isDefault && <span className="op-chip op-chip--accent px-1.5 py-0">Default for new plans</span>}
          <span className="op-meta">
            {set.published_at ? `Published${set.published_by_display_name ? ` by ${set.published_by_display_name}` : ""}` : `Started by ${set.created_by_display_name ?? "AI PRIME"}`}
            {set.retired_at ? ` · retired${set.retired_by_display_name ? ` by ${set.retired_by_display_name}` : ""}` : ""}
          </span>
        </div>
        {manage && set.status !== "retired" && (
          <div className="flex flex-wrap gap-2 pt-1" role="group" aria-label="Set actions">
            {draft && !renaming && <button type="button" className="op-btn op-btn--sm" onClick={() => setRenaming(true)}>Rename</button>}
            {draft && <button type="button" className="op-btn op-btn--primary op-btn--sm" onClick={publish}>Publish</button>}
            {draft && <button type="button" className="op-btn op-btn--sm" onClick={discard}>Discard draft</button>}
            {set.status === "published" && (
              <>
                <button type="button" className="op-btn op-btn--sm" onClick={() => act(() => newTemplateSetVersion(set.id))}>New version</button>
                {isDefault
                  ? <button type="button" className="op-btn op-btn--sm" onClick={() => act(() => setDefaultTemplateLineage(null), { select: false })}>Stop using as default</button>
                  : <button type="button" className="op-btn op-btn--sm" onClick={() => act(() => setDefaultTemplateLineage(set.lineage_id), { select: false })}>Use as default</button>}
                <button type="button" className="op-btn op-btn--sm" onClick={retire}>Retire</button>
              </>
            )}
          </div>
        )}
        {manage && set.status !== "draft" && (
          <p className="op-meta">{set.status === "published"
            ? "Published sets can't be edited. New version starts a draft copy you can change and publish."
            : "Retired: plans created with it keep it; new plans don't get it."}</p>
        )}
      </div>

      <table className="w-full text-left text-[12.5px]">
        <thead>
          <tr className="op-label uppercase">
            <th className="py-1.5 pr-2 font-normal" aria-label="Preview" />
            <th className="py-1.5 pr-2 font-normal">Name</th>
            <th className="py-1.5 pr-2 font-normal">Key</th>
            <th className="py-1.5 pr-2 font-normal">Category</th>
            <th className="py-1.5 pr-2 font-normal">Size</th>
            <th className="py-1.5 pr-2 font-normal">Layer</th>
            <th className="py-1.5 pr-2 font-normal">Source</th>
            {draft && <th className="py-1.5 font-normal" aria-label="Actions" />}
          </tr>
        </thead>
        <tbody>
          {templates.map((t) => (editingId === t.id ? (
            <tr key={t.id}><td colSpan={8} className="py-2">
              <TemplateForm template={t} onCancel={() => setEditingId(null)} onSubmit={async (body) => {
                if (await act(() => updateTemplate(set.id, t.id, body))) setEditingId(null);
              }} />
            </td></tr>
          ) : (
            <tr key={t.id} className="border-t border-bg-border" data-testid="pp-library-template">
              <td className="py-1.5 pr-2"><TemplatePreview t={t} w={36} h={22} /></td>
              <td className="py-1.5 pr-2 text-fg-primary">{t.name}</td>
              <td className="py-1.5 pr-2 op-mono text-fg-secondary">{t.key}</td>
              <td className="py-1.5 pr-2">{CATEGORY_LABELS[t.category] ?? t.category}</td>
              <td className="py-1.5 pr-2">{templateDims(t)}</td>
              <td className="py-1.5 pr-2 op-mono">{t.default_layer}</td>
              <td className="py-1.5 pr-2 text-fg-secondary">{t.dims_source ?? NO_SOURCE_TEXT}</td>
              {draft && (
                <td className="whitespace-nowrap py-1.5 text-right">
                  <button type="button" className="op-meta mr-2 hover:text-fg-primary" aria-label={`Edit ${t.name}`} onClick={() => setEditingId(t.id)}>Edit</button>
                  <button type="button" className="op-meta hover:text-fg-primary" aria-label={`Remove ${t.name}`} onClick={async () => {
                    if (window.confirm(`Remove ${t.name} from this draft?`)) await act(() => removeTemplate(set.id, t.id));
                  }}>Remove</button>
                </td>
              )}
            </tr>
          )))}
        </tbody>
      </table>
      {templates.length === 0 && <p className="op-meta">No templates yet.{draft ? " Add one below; a set needs at least one to be published." : ""}</p>}

      {draft && (
        <section className="flex flex-col gap-2 border-t border-bg-border pt-3" aria-label="Add a template">
          <SectionLabel>Add a template</SectionLabel>
          <TemplateForm onSubmit={(body) => act(() => addTemplate(set.id, body))} resetOnSuccess />
        </section>
      )}
    </div>
  );
}

const num = (v) => (v === "" || v == null ? undefined : Number(v));

/**
 * Add (no template) or edit (a template: only what changed is sent; kind is
 * fixed). Footprints are rectangles the server builds from length and width,
 * centered on the anchor, length along the plan's north before rotation.
 */
function TemplateForm({ template = null, onSubmit, onCancel = null, resetOnSuccess = false }) {
  const blank = { key: "", name: "", category: "tank", kind: "footprint", length_ft: "", width_ft: "", radius_ft: "", default_layer: "", dims_source: "" };
  const from = template ? {
    key: template.key, name: template.name, category: template.category, kind: template.kind,
    length_ft: template.length_ft ?? "", width_ft: template.width_ft ?? "", radius_ft: template.radius_ft ?? "",
    default_layer: template.default_layer ?? "", dims_source: template.dims_source ?? "",
  } : blank;
  const [v, setV] = useState(from);
  const set = (k) => (e) => setV((cur) => ({ ...cur, [k]: e.target.value }));

  function body() {
    const all = {
      key: v.key.trim(), name: v.name.trim(), category: v.category,
      ...(v.kind === "footprint" ? { length_ft: num(v.length_ft), width_ft: num(v.width_ft) } : { radius_ft: num(v.radius_ft) }),
      default_layer: v.default_layer.trim() || undefined,
      dims_source: v.dims_source.trim() || (template?.dims_source ? null : undefined),
    };
    if (!template) return { ...all, kind: v.kind };
    // An edit sends what changed, nothing else.
    const out = {};
    for (const [k, val] of Object.entries(all)) {
      if (val === undefined) continue;
      if ((template[k] ?? null) !== val) out[k] = val;
    }
    return out;
  }

  return (
    <form className="flex flex-wrap items-end gap-2" aria-label={template ? `Edit ${template.name}` : "Add a template"} onSubmit={async (e) => {
      e.preventDefault();
      const b = body();
      if (template && Object.keys(b).length === 0) { onCancel?.(); return; }
      const ok = await onSubmit(b);
      if (ok && resetOnSuccess) setV(blank);
    }}>
      <label className="flex flex-col gap-1"><span className="op-label uppercase">Name</span>
        <input className={`${field} w-40`} value={v.name} maxLength={80} onChange={set("name")} /></label>
      <label className="flex flex-col gap-1"><span className="op-label uppercase">Key</span>
        <input className={`${field} w-32 op-mono`} value={v.key} maxLength={48} onChange={set("key")} /></label>
      <label className="flex flex-col gap-1"><span className="op-label uppercase">Category</span>
        <select className={field} value={v.category} onChange={set("category")}>
          {TEMPLATE_CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
        </select></label>
      {!template && (
        <label className="flex flex-col gap-1"><span className="op-label uppercase">Kind</span>
          <select className={field} value={v.kind} onChange={set("kind")}>
            <option value="footprint">Footprint</option>
            <option value="zone">Zone</option>
          </select></label>
      )}
      {v.kind === "footprint" ? (
        <>
          <label className="flex flex-col gap-1"><span className="op-label uppercase">Length (ft)</span>
            <input type="number" step="any" min="0.5" max="1000" className={`${field} w-24`} value={v.length_ft} onChange={set("length_ft")} /></label>
          <label className="flex flex-col gap-1"><span className="op-label uppercase">Width (ft)</span>
            <input type="number" step="any" min="0.5" max="1000" className={`${field} w-24`} value={v.width_ft} onChange={set("width_ft")} /></label>
        </>
      ) : (
        <label className="flex flex-col gap-1"><span className="op-label uppercase">Radius (ft)</span>
          <input type="number" step="any" min="1" max="2640" className={`${field} w-24`} value={v.radius_ft} onChange={set("radius_ft")} /></label>
      )}
      <label className="flex flex-col gap-1"><span className="op-label uppercase">Layer</span>
        <input className={`${field} w-28 op-mono`} value={v.default_layer} maxLength={40} placeholder="category" onChange={set("default_layer")} /></label>
      <label className="flex flex-col gap-1"><span className="op-label uppercase">Source</span>
        <input className={`${field} w-56`} value={v.dims_source} maxLength={500} placeholder="Where the size comes from" onChange={set("dims_source")} /></label>
      <button type="submit" className="op-btn op-btn--primary op-btn--sm">{template ? "Save" : "Add"}</button>
      {onCancel && <button type="button" className="op-btn op-btn--sm" onClick={onCancel}>Cancel</button>}
    </form>
  );
}
